/* eslint-disable react/no-multi-comp */
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Fab, Grid, type Theme, useMediaQuery } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as R from 'ramda'
import {
  Defaults,
  PermissionArea,
  type PrimitiveTableColumn,
  type TableFilter,
  Utils,
} from '@pbt/pbt-ui-components'
import type { ConstantWithColors } from '@pbt/pbt-ui-components/src/types/entities'

import Avatar from '~/components/common/Avatar'
import PuiButtonGroup, {
  type PuiButtonGroupItem,
} from '~/components/common/buttons/PuiButtonGroup'
import QuickFilterButton from '~/components/common/buttons/QuickFilterButton'
import ClientsFilter from '~/components/common/filters/ClientsFilter'
import TeamMemberFilter from '~/components/common/filters/TeamMemberFilter'
import ExpandableTable from '~/components/common/lists/ExpandableTable'
import ListSearchFilterPanel from '~/components/common/lists/ListSearchFilterPanel'
import AssignedCell from '~/components/common/lists/primitive-table/cells/AssignedCell'
import CreatedByCell from '~/components/common/lists/primitive-table/cells/CreatedByCell'
import DueDateCell from '~/components/common/lists/primitive-table/cells/DueDateCell'
import PrimitiveTableWithSearchHighlights from '~/components/common/lists/primitive-table/PrimitiveTableWithSearchHighlights'
import { TaskStatusSelectV2 } from '~/components/dashboard/tasks-dashboard/TaskStatusSelectV2'
import DialogNames from '~/constants/DialogNames'
import { NotificationAreaNames } from '~/constants/notifications'
import i18n from '~/locales/i18n'
import {
  fetchMoreItemsForTasksList,
  fetchTasksList,
  setTasksListFilters,
} from '~/store/actions/tasks'
import { useGetTaskIsUnlinkedRxRequest } from '~/store/hooks/tasks'
import {
  getCRUDByArea,
  getCurrentBusinessWellnessPlansEnabled,
  getCurrentUser,
} from '~/store/reducers/auth'
import { getTaskStates } from '~/store/reducers/constants'
import {
  getTasksIsLoadingList,
  getTasksList,
  getTasksListFilters,
  getTasksTotalCount,
  getTasksValidationError,
} from '~/store/reducers/tasks'
import type { Task } from '~/types'
import { addSearch, getUrlSearchParam } from '~/utils'
import useDialog from '~/utils/useDialog'
import useEffectExceptOnMount from '~/utils/useEffectExceptOnMount'
import useErrorAlert from '~/utils/useErrorAlert'

import LinkedItemNotificationsBadge from '../notifications/LinkedItemNotificationsBadge'
import NoTasksScreen from './NoTasksScreen'
import TaskDetails from './TaskDetails'
import TaskPatientCell from './TaskPatientCell'
import TasksDateRangeFilter from './TasksDateRangeFilter'
import TasksStatusFilter from './TasksStatusFilter'
import TasksStatusFilterV2 from './TasksStatusFilterV2'
import TasksTableRow from './TasksTableRow'
import TasksTypeFilter from './TasksTypeFilter'
import TaskTypeCell from './TaskTypeCell'

type TaskFilterState = {
  assignedTo?: string
  forClient?: boolean
  statuses?: string[]
} & PuiButtonGroupItem

const useStyles = makeStyles(
  (theme) => ({
    button: {
      minWidth: 132,
      height: 40,
    },
    quickFilterButton: {
      minWidth: 156,
      [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
      },
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(1),
      },
    },
    quickFilterButtonSearch: {
      marginLeft: 0,
    },
  }),
  { name: 'TasksTableComponent' },
)

const Filters = {
  DUE_DATE: 'dueDate',
  ASSIGNED_TO: 'assignedTo',
  CREATED_BY: 'createdBy',
  TYPE: 'type',
  STATUSES: 'statuses',
}

const dueDateColumn: PrimitiveTableColumn = {
  label: i18n.t('Time:LABEL.DUE_DATE'),
  prop: DueDateCell,
  filter: Filters.DUE_DATE,
  FilterComponent: (props) => (
    <TasksDateRangeFilter {...props} showInChronologicalOrder />
  ),
  width: 2,
}

const taskColumn: PrimitiveTableColumn = {
  label: i18n.t('Common:TASK'),
  prop: 'name',
  highlight: true,
  width: 3,
}

const createdByColumn: PrimitiveTableColumn = {
  label: i18n.t('Common:CREATED_BY'),
  prop: CreatedByCell,
  filter: Filters.CREATED_BY,
  FilterComponent: TeamMemberFilter,
  width: 3,
}

const typeColumn: PrimitiveTableColumn = {
  label: i18n.t('Common:TYPE_ONE'),
  prop: TaskTypeCell,
  filter: Filters.TYPE,
  FilterComponent: TasksTypeFilter,
  width: 2,
}

const assignedColumn: PrimitiveTableColumn = {
  label: i18n.t('Common:ASSIGNED'),
  hasWarning: (item) => item.assigned === null,
  prop: AssignedCell,
  filter: Filters.ASSIGNED_TO,
  FilterComponent: ({ ...props }) => (
    <TeamMemberFilter displayEmpty {...props} />
  ),
  width: 3,
}

const assignedColumnForClient: PrimitiveTableColumn = {
  label: `${i18n.t('Common:ASSIGNED')}/${i18n
    .t('Common:PET_PARENT')
    .toLowerCase()}`,
  hasWarning: (item) => item.assigned === null,
  prop: AssignedCell,
  filter: Filters.ASSIGNED_TO,

  FilterComponent: ({ ...props }) => (
    <ClientsFilter
      displayEmpty
      label={i18n.t('Search:PET_PARENTS')}
      {...props}
    />
  ),
  width: 3,
}

const notificationColumn: PrimitiveTableColumn = {
  noTypography: true,
  prop: (task) => (
    <LinkedItemNotificationsBadge
      areaName={NotificationAreaNames.TASKS}
      itemId={task.id}
    />
  ),
  width: 0.25,
}

const getStatusColumn = (withFilter: boolean) => ({
  label: i18n.t('Common:STATUS'),
  noTypography: true,
  filter: withFilter ? Filters.STATUSES : undefined,
  prop: (task: Task) => (
    <TaskStatusSelectV2
      hideDue
      task={task}
      onClick={(event) => event.stopPropagation()}
    />
  ),
  FilterComponent: withFilter ? TasksStatusFilterV2 : undefined,
  width: 2,
})

const statusColumnForClient = {
  label: i18n.t('Common:STATUS'),
  noTypography: true,
  filter: Filters.STATUSES,
  prop: (task: Task) => (
    <TaskStatusSelectV2
      hideDue
      task={task}
      onClick={(event) => event.stopPropagation()}
    />
  ),
  FilterComponent: TasksStatusFilter,
  width: 2,
}

export const useTaskTableColumns = (
  forClient: boolean,
  isToDo: boolean,
  isExpanded: boolean,
) => {
  const getTaskIsUnlinkedRxRequest = useGetTaskIsUnlinkedRxRequest()

  const patientColumn: PrimitiveTableColumn = {
    hasWarning: getTaskIsUnlinkedRxRequest,
    label: i18n.t('Common:PATIENT'),
    noTypography: true,
    prop: (task: Task) => <TaskPatientCell task={task} />,
    width: 2,
    columnWrap: 'nowrap',
  }

  const todoColumns = isExpanded
    ? [
        dueDateColumn,
        taskColumn,
        notificationColumn,
        getStatusColumn(true),
        typeColumn,
        patientColumn,
        assignedColumn,
        createdByColumn,
      ]
    : [
        dueDateColumn,
        notificationColumn,
        getStatusColumn(true),
        typeColumn,
        taskColumn,
        patientColumn,
        assignedColumn,
        createdByColumn,
      ]

  const doneColumns = isExpanded
    ? [
        dueDateColumn,
        taskColumn,
        notificationColumn,
        getStatusColumn(false),
        typeColumn,
        patientColumn,
        assignedColumn,
        createdByColumn,
      ]
    : [
        dueDateColumn,
        notificationColumn,
        getStatusColumn(false),
        typeColumn,
        taskColumn,
        patientColumn,
        assignedColumn,
        createdByColumn,
      ]

  const clientColumns = isExpanded
    ? [
        dueDateColumn,
        taskColumn,
        notificationColumn,
        statusColumnForClient,
        typeColumn,
        patientColumn,
        assignedColumnForClient,
        createdByColumn,
      ]
    : [
        dueDateColumn,
        notificationColumn,
        statusColumnForClient,
        typeColumn,
        taskColumn,
        patientColumn,
        assignedColumnForClient,
        createdByColumn,
      ]

  if (isToDo) {
    return todoColumns
  }

  if (forClient) {
    return clientColumns
  }

  return doneColumns
}

interface TasksTableComponentProps {
  isExpandable?: boolean
  onDetailsClose: () => void
  taskId?: string
}

const TasksTableComponentV2 = ({
  isExpandable,
  taskId,
  onDetailsClose,
}: TasksTableComponentProps) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const list = useSelector(getTasksList)
  const isLoading = useSelector(getTasksIsLoadingList)
  const totalCount = useSelector(getTasksTotalCount)
  const filters = useSelector(getTasksListFilters)
  const currentUser = useSelector(getCurrentUser)
  const permissions = useSelector(getCRUDByArea(PermissionArea.TASK))
  const wellnessPlansEnabled = useSelector(
    getCurrentBusinessWellnessPlansEnabled,
  )
  const TaskStates: ConstantWithColors[] = useSelector(getTaskStates)

  const { t } = useTranslation(['Common', 'Search', 'Tasks', 'Time'])

  const DoneState = Utils.findConstantIdByName('Done', TaskStates)

  const DoneStatuses = [DoneState]
  const ToDoStatuses = TaskStates.filter(({ id }) => id !== DoneState).map(
    (item) => item?.id,
  )

  const todoLabel = t('Tasks:LABEL.TO_DO')
  const TaskFilterStates: TaskFilterState[] = [
    { label: todoLabel, statuses: ToDoStatuses, forClient: false },
    { label: t('Common:DONE'), statuses: DoneStatuses, forClient: false },
  ]

  if (wellnessPlansEnabled) {
    TaskFilterStates.push({
      label: t('Tasks:TASKS_TABLE_COMPONENT.CLIENT_TASKS'),
      icon: undefined,
      forClient: true,
    })
  }

  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))

  const [openTaskDialog] = useDialog(DialogNames.TASK)

  const search = getUrlSearchParam('query', location.search)

  useErrorAlert({
    errorSelector: getTasksValidationError,
    onOpenHandler: () => {
      navigate(addSearch(location, '/tasks-dashboard/'))
    },
    onCloseHandler: () => {
      dispatch(
        fetchTasksList(0, Defaults.INFINITE_LIST_BATCH_LOAD_COUNT, search),
      )
    },
  })

  const setFilters = (newFilters: Record<string, TableFilter>) => {
    dispatch(setTasksListFilters(newFilters))
  }

  const setInitialFilters = () => {
    setFilters({
      statuses: {
        value: ToDoStatuses,
      },
      forClient: {
        value: false,
      },
    })
  }

  const onApplyFilter = (filter: string, value: TableFilter) => {
    const isAssignedToFilter = filter === 'assignedTo'
    const shouldAddSharedToPersonIds =
      isAssignedToFilter &&
      typeof value.value === 'string' &&
      value.value.includes(currentUser.id)

    const newFilters: Record<string, TableFilter> = {
      ...filters,
      [filter]: value,
    }
    if (shouldAddSharedToPersonIds) {
      newFilters.sharedToPersonIds = { value: currentUser.id }
    } else if (isAssignedToFilter) {
      newFilters.sharedToPersonIds = { value: null }
    }
    setFilters(newFilters)
  }

  const onClearFilters = () => {
    setInitialFilters()
  }

  useEffect(() => {
    setInitialFilters()
  }, [])

  useEffectExceptOnMount(() => {
    dispatch(fetchTasksList(0, Defaults.INFINITE_LIST_BATCH_LOAD_COUNT, search))
  }, [search, filters])

  const navigateToTask = (id: string) => {
    navigate(addSearch(location, `/tasks-dashboard/${id}`))
  }

  const isItemLoaded = (index: number) => Boolean(list[index])

  const loadMoreItems = (startIndex: number, endIndex: number) => {
    dispatch(fetchMoreItemsForTasksList(startIndex, endIndex, search))
  }

  const selectedItem =
    filters.forClient?.value === true && wellnessPlansEnabled
      ? TaskFilterStates.find((state) => state.forClient)
      : TaskFilterStates.find((state) =>
          state.statuses?.every((status) =>
            ((filters.statuses?.value as string[]) || []).includes(status),
          ),
        ) || R.head(TaskFilterStates)

  const forClient = Boolean(selectedItem?.forClient)
  const isToDo =
    selectedItem?.forClient === false && selectedItem.label === todoLabel

  const specialFilters = [
    // for the `Boop tasks` tab `statues` filter becomes column filter
    forClient ? undefined : 'statuses',
    'forClient',
  ]

  const hasAppliedFilters = (appliedFilters: Record<string, TableFilter>) =>
    Object.keys(appliedFilters).filter(
      (filterName) =>
        Boolean(appliedFilters[filterName]) &&
        !specialFilters.includes(filterName),
    ).length > 0

  const hasSelectedFilters = hasAppliedFilters(filters)

  const columns = useTaskTableColumns(forClient, isToDo, Boolean(taskId))

  const handleSelectedTabFilterChange = (selectedFilter: TaskFilterState) => {
    setFilters({
      ...filters,
      statuses: {
        value: selectedFilter.statuses,
      },
      forClient: {
        ...filters.forClient,
        value: selectedFilter.forClient,
      },
      assignedTo: selectedFilter.forClient
        ? { value: null }
        : filters.assignedTo,
    })
  }

  const headerButtons = (
    <Grid
      container
      item
      xs
      alignItems={isMobile ? 'stretch' : 'center'}
      direction={isMobile ? 'column' : 'row'}
    >
      {!search && (
        <PuiButtonGroup
          items={TaskFilterStates}
          selectedItem={selectedItem}
          onItemSelected={handleSelectedTabFilterChange}
        />
      )}
      {!filters.forClient?.value && (
        <Grid item>
          <QuickFilterButton
            IconComponent={Avatar}
            IconProps={{
              person: currentUser,
            }}
            active={filters.assignedTo?.value === currentUser.id}
            className={classNames(
              classes.quickFilterButton,
              search && classes.quickFilterButtonSearch,
            )}
            text={t('Common:ASSIGNED_TO_ME')}
            onClick={() => {
              const wasChecked = filters.assignedTo?.value === currentUser.id
              setFilters({
                ...filters,
                assignedTo: wasChecked
                  ? { value: null }
                  : {
                      ...filters.assignedTo,
                      value: currentUser.id,
                      humanReadable: Utils.getPersonString(currentUser),
                    },
                sharedToPersonIds: wasChecked
                  ? { value: null }
                  : {
                      ...filters.sharedToPersonIds,
                      value: currentUser.id,
                    },
              })
            }}
          />
        </Grid>
      )}
      <Grid ml={2}>
        <ListSearchFilterPanel isLoading={isLoading} searchCount={totalCount} />
      </Grid>
    </Grid>
  )

  const wrappedHeaderButtons = (
    <Grid container item px={3} py={2}>
      {headerButtons}
      {permissions.create && (
        <Fab
          className={classes.button}
          color="inherit"
          variant="extended"
          onClick={() =>
            openTaskDialog({
              redirectOnCreated: true,
              forClientActive: forClient,
            })
          }
        >
          {t('Tasks:LABEL.NEW_TASK')}
        </Fab>
      )}
    </Grid>
  )

  return (
    <ExpandableTable
      Expander={isExpandable ? TaskDetails : undefined}
      ExpanderProps={{ isChirp: true }}
      NoItemsScreen={NoTasksScreen}
      NoItemsScreenProps={{
        headerButtons,
      }}
      hasSelectedFilters={hasSelectedFilters}
      headerButtons={wrappedHeaderButtons}
      isLoading={isLoading}
      itemId={taskId}
      list={list}
      searchTerm={search}
      title={t('Common:TASKS')}
      onClose={onDetailsClose}
      onSelected={navigateToTask}
    >
      <PrimitiveTableWithSearchHighlights
        HeaderProps={{ useNilAsEmpty: true }}
        RowComponent={TasksTableRow}
        columns={columns}
        filters={filters}
        isItemLoaded={isItemLoaded}
        list={list}
        loadMoreItems={loadMoreItems}
        mainColumnCount={2}
        totalCount={totalCount}
        onApplyFilter={onApplyFilter}
        onClearFilters={onClearFilters}
      />
    </ExpandableTable>
  )
}

export default TasksTableComponentV2
